import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { ReactComponent as LapturaCar } from "../../assets/brand/laptura-car.svg";
import { ReactComponent as LapturaWordmark } from "../../assets/brand/laptura-wordmark.svg";
import { useMeasurementSystem } from "../../contexts/SpeedUnitContext";
import { formatSmallDistance, getDistanceLabel, getSpeedLabel } from "../../lib/utils";

export function AuthFooter() {
  return (
    <div className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-xs text-lap-dim sm:mt-6">
      <span>© 2026 Laptura</span>
      <a className="transition hover:text-lap-text" href="https://www.laptura.com">About Laptura</a>
      <Link className="transition hover:text-lap-text" to="/Laptura/legal">Terms and waiver</Link>
    </div>
  );
}

export function AuthShell({ children }) {
  const brandPanelRef = useRef(null);
  const { measurementSystem } = useMeasurementSystem();

  const moveSpotlight = (event) => {
    const panel = brandPanelRef.current;
    if (!panel || event.pointerType === "touch") return;
    const bounds = panel.getBoundingClientRect();
    panel.style.setProperty("--auth-pointer-x", `${event.clientX - bounds.left}px`);
    panel.style.setProperty("--auth-pointer-y", `${event.clientY - bounds.top}px`);
  };

  return (
    <div className="h-screen h-[100dvh] overflow-y-auto bg-lap-bg">
      <div className="min-h-full lg:grid lg:grid-cols-[minmax(0,1.15fr)_minmax(27rem,0.85fr)]">
        <section
          ref={brandPanelRef}
          onPointerMove={moveSpotlight}
          className="auth-brand-panel relative hidden overflow-hidden border-r border-lap-hairline bg-lap-card lg:sticky lg:top-0 lg:flex lg:h-screen lg:flex-col lg:justify-between lg:p-12 xl:p-16"
        >
          <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.035) 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
          <div className="auth-pointer-glow absolute inset-0 pointer-events-none" aria-hidden="true" />
          <div className="relative z-10 w-full max-w-xl self-center">
            <LapturaWordmark className="auth-brand-logo h-10 w-auto text-white" role="img" aria-label="Laptura" />
          </div>

          <div className="auth-brand-copy relative z-10 w-full max-w-xl self-center">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-lap-accent-bright">Performance, decoded</p>
            <h1 className="max-w-lg text-5xl font-semibold leading-[1.05] tracking-tight text-white xl:text-6xl">Every lap leaves a line.</h1>
            <p className="auth-brand-lede mt-6 max-w-lg text-lg leading-8 text-lap-muted">Turn telemetry into a clear plan for the next session, corner by corner and lap by lap.</p>

            <div className="auth-logger-strip mt-7 flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-[0.14em] text-lap-dim">
              <span className="mr-1">Works with</span>
              {['AIM', 'VBOX', 'MoTeC', 'XRK', 'iRacing'].map((format) => (
                <span key={format} className="rounded-full border border-lap-hairline bg-lap-bg/55 px-3 py-1.5 text-lap-muted">
                  {format}
                </span>
              ))}
            </div>

            <div className="auth-preview mt-8 overflow-hidden rounded-2xl border border-lap-hairline bg-lap-bg/70 p-6 shadow-2xl shadow-black/30 backdrop-blur">
              <div className="mb-4 flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.2em] text-lap-dim">
                <span>Sample session · COTA</span>
                <span className="text-lap-accent-bright">Lap 9 · Best</span>
              </div>
              <div className="mb-2 flex items-center justify-between text-[10px] uppercase tracking-[0.14em] text-lap-dim">
                <span>Speed · {getSpeedLabel(measurementSystem)} vs distance ({getDistanceLabel(measurementSystem)})</span>
                <span className="flex gap-3 normal-case tracking-normal">
                  <span><i className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-lap-accent" />Best</span>
                  <span><i className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-lap-muted" />Average</span>
                </span>
              </div>
              <svg viewBox="0 0 620 160" className="auth-preview-chart h-auto w-full" aria-label="Sample best-lap and average speed traces">
                <defs>
                  <linearGradient id="auth-trace" x1="0" x2="1">
                    <stop offset="0" stopColor="rgb(var(--lap-accent))" stopOpacity="0.15" />
                    <stop offset="0.45" stopColor="rgb(var(--lap-accent-bright))" />
                    <stop offset="1" stopColor="rgb(var(--lap-accent))" stopOpacity="0.35" />
                  </linearGradient>
                </defs>
                <g stroke="rgb(var(--lap-hairline))" strokeDasharray="3 7" opacity="0.65">
                  <line x1="0" x2="620" y1="38" y2="38" />
                  <line x1="0" x2="620" y1="80" y2="80" />
                  <line x1="0" x2="620" y1="122" y2="122" />
                </g>
                <path d="M8 124 C60 116 85 47 142 52 S214 143 282 117 353 30 421 48 500 141 612 76" fill="none" stroke="rgb(var(--lap-muted))" strokeWidth="2" strokeLinecap="round" opacity="0.65" />
                <path d="M8 121 C62 111 87 38 145 43 S218 134 286 108 356 23 425 39 503 132 612 68" fill="none" stroke="url(#auth-trace)" strokeWidth="3" strokeLinecap="round" />
                <line x1="425" x2="425" y1="14" y2="148" stroke="rgb(var(--lap-accent-bright))" strokeWidth="1" opacity="0.55" />
                <circle cx="425" cy="39" r="5" fill="rgb(var(--lap-accent-bright))" stroke="white" strokeWidth="1.5" />
              </svg>
              <div className="mt-3 grid grid-cols-3 gap-4 border-t border-lap-hairline pt-4">
                <div><p className="text-[10px] uppercase tracking-wider text-lap-dim">Best lap</p><p className="mt-1 font-mono text-sm text-white">2:09.184</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-lap-dim">To last</p><p className="mt-1 font-mono text-sm text-lap-gain">-0.412 s</p></div>
                <div><p className="text-[10px] uppercase tracking-wider text-lap-dim">Max lateral</p><p className="mt-1 font-mono text-sm text-white">1.42 g</p></div>
              </div>
              <div className="auth-preview-coach mt-4 flex gap-3 rounded-xl border border-lap-accent/20 bg-lap-accent/10 p-3.5">
                <LapturaCar className="mt-0.5 h-5 w-auto shrink-0 text-lap-accent-bright" aria-hidden="true" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-lap-accent-bright">Coach · T8</p>
                  <p className="mt-1 text-xs leading-5 text-lap-text">Brake onset is {formatSmallDistance(9, measurementSystem)} later than your best reference. Settle the car earlier to protect apex speed.</p>
                </div>
              </div>
            </div>
          </div>

          <p className="auth-brand-footer relative z-10 w-full max-w-xl self-center text-xs text-lap-dim">Built for everyone who wants clearer answers from racing data.</p>
        </section>

        <section className="flex min-h-[100dvh] justify-center px-4 pt-[max(0.75rem,env(safe-area-inset-top,0px))] pb-[max(0.75rem,env(safe-area-inset-bottom,0px))] sm:px-8 sm:py-8 lg:bg-lap-bg">
          <div className="my-auto flex w-full justify-center">{children}</div>
        </section>
      </div>
    </div>
  );
}
